import { Bar, BarChart, CartesianGrid, LabelList, XAxis } from "recharts";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/lib/shadcn/ui/chart";
import { Table, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import PercentageChangeText from "@/lib/shadcn/components/percentage-change-text";

const CustomLabel = ({ x, y, value, barValueFormatter = (v: number) => v.toFixed(1) }: any) => {
    return (
        <text x={x + 20} y={y - 10} fill="#6B7280" textAnchor="middle" fontSize={12} fontWeight="500">
            {barValueFormatter(value)}
        </text>
    );
};

export interface SinglePointMultiBarChartData {
    [key: string]: number;
}

export interface SinglePointMultiBarChartProps {
    description: string;
    title: string;
    data: SinglePointMultiBarChartData;
    dataPrevious?: SinglePointMultiBarChartData;
    config: ChartConfig;
    tickLabel: string;
    barValueFormatter?: (value: number) => string;
}

export function SinglePontMultiBarChart({
    barValueFormatter = (value) => value.toFixed(1),
    config,
    data,
    dataPrevious,
    description,
    tickLabel,
    title,
}: SinglePointMultiBarChartProps) {
    const chartData = [
        {
            tickLabel: tickLabel,
            ...data,
        },
    ];

    return (
        <Card className="w-full">
            <CardHeader className="space-y-0 pb-2">
                <CardTitle className="text-base font-medium">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
                <ChartContainer config={config} className={"max-h-80 w-full"}>
                    <BarChart data={chartData} margin={{ top: 30, right: 30, left: 30, bottom: 5 }} barGap={20}>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" />
                        <XAxis dataKey="tickLabel" tickLine={false} tickMargin={10} axisLine={false} />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dashed" />} />
                        {Object.keys(data).map((k) => (
                            <Bar key={k} dataKey={k} fill={`var(--color-${k})`} radius={4} barSize={40}>
                                <LabelList dataKey={k} content={<CustomLabel barValueFormatter={barValueFormatter} />} position="top" />
                            </Bar>
                        ))}
                    </BarChart>
                </ChartContainer>
            </CardContent>
            {dataPrevious && (
                <CardFooter className="flex-col gap-2 text-sm mt-4">
                    <div className="leading-none text-muted-foreground text-center">
                        Compared to Previous Year
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className={"text-lef"}>Lead Days</TableHead>
                                    <TableHead className={"text-right"}>Value</TableHead>
                                    <TableHead className={"text-right"}>% Change</TableHead>
                                </TableRow>
                                {Object.keys(data).map((group) => {
                                    const currentVal = data[group];
                                    const prevVal: number = dataPrevious[group] || 0;
                                    const percentageChange = prevVal === 0 ? 0 : ((currentVal - prevVal) / prevVal) * 100;
                                    return (
                                        <TableRow key={group}>
                                            <TableCell className={"text-left"}>{config[group].label}</TableCell>
                                            <TableCell className={"text-right"}>{barValueFormatter(prevVal)}</TableCell>
                                            <TableCell className={"text-right"}>
                                                <PercentageChangeText
                                                    value={percentageChange}
                                                    valueFormatter={() => Math.round(percentageChange).toString()}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableHeader>
                        </Table>
                    </div>
                </CardFooter>
            )}
        </Card>
    );
}
